/**
 * Vertical Bar Chart Type Definition
 */

import {
  colorControls,
  numCategoriesControl,
  barOrderingControl,
  labelRotationControl,
  truncateXAxisLabelsControl,
  gridControl,
  showValuesControl,
  valueDecimalsControl,
  axisControls,
  referenceLineControls,
  legendControls,
} from '../shared/controls';

import {
  matplotlibSetup,
  createFigure,
  titleCode,
  gridCode,
  axisLabelsCode,
  yAxisRangeCode,
  referenceLineCode,
  labelRotationCode,
  spineCode,
  finishCode,
  sortingCode,
  valueLabelsCode,
} from '../shared/codeSnippets';

import {
  seabornSetup,
  seabornCreateFigure,
  seabornTitleCode,
  seabornGridCode,
  seabornAxisLabelsCode,
  seabornYAxisRangeCode,
  seabornReferenceLineCode,
  seabornLabelRotationCode,
  seabornSpineCode,
  seabornFinishCode,
} from '../shared/seabornSnippets';

import { generateColorCode, getColors } from '../shared/palettes';

// Generate dynamic sample data based on numCategories
function generateSampleData(n) {
  const data = [];
  for (let i = 1; i <= n; i++) {
    data.push({ name: `Cat ${i}`, value: 75 + Math.random() * 20 });
  }
  return data;
}

const verticalBar = {
  id: 'vertical-bar',
  name: 'Vertical Bar Chart',
  category: 'bar',
  description: 'Compare values across categories',
  
  // Dynamic sample data based on config
  getSampleData: (config) => generateSampleData(config.numCategories || 5),
  
  // Static fallback for initial render
  sampleData: [
    { name: 'Cat 1', value: 85.2 },
    { name: 'Cat 2', value: 78.5 },
    { name: 'Cat 3', value: 92.1 },
    { name: 'Cat 4', value: 88.7 },
    { name: 'Cat 5', value: 81.3 },
  ],
  
  defaultConfig: {
    externalColorMode: 'single',
    color: '#4C6EE6',
    useBluesPalette: false,
    useRedsPalette: false,
    useGreensPalette: false,
    numCategories: 5,
    outputFormat: 'matplotlib',
    labelRotation: null,
    truncateXAxisLabels: false,
    showGrid: true,
    showValues: false,
    valueDecimals: 1,
    ylabel: 'Value',
    xlabel: 'Category',
    title: '',
    yMin: '',
    yMax: '',
    showReferenceLine: false,
    referenceValue: '',
    referenceLabel: 'Baseline',
    barOrdering: 'original',
    showLegend: false,
    legendPosition: 'top',
    colorOverrides: undefined,
  },
  
  controls: [
    ...colorControls,
    numCategoriesControl,
    barOrderingControl,
    labelRotationControl,
    truncateXAxisLabelsControl,
    gridControl,
    showValuesControl,
    valueDecimalsControl,
    ...legendControls,
    ...axisControls,
    ...referenceLineControls,
  ],
  
  generateCode: (config, dataContext, s) => {
    const hasUserData = dataContext?.isLoaded && dataContext.mapping?.category && dataContext.mapping?.value;
    const hasColorBy = hasUserData && dataContext.mapping?.colorBy;
    const n = config.numCategories || 5;
    const categories = Array.from({ length: n }, (_, i) => `Cat ${i + 1}`);
    const values = Array.from({ length: n }, () => (75 + Math.random() * 20).toFixed(1));
    
    const colors = getColors(config, n, s);
    const usesPalette = !!(config.primaryColor || config.useBluesPalette || config.useRedsPalette || config.useGreensPalette);
    const hasOverrides = config.colorOverrides && Object.keys(config.colorOverrides).length > 0;
    const overridesCode = hasOverrides
      ? `\n# Apply color overrides\ncolor_overrides = ${JSON.stringify(config.colorOverrides)}\nbar_colors = [color_overrides.get(cat, bar_colors[i % len(bar_colors)] if isinstance(bar_colors, list) else bar_colors) for i, cat in enumerate(categories)]`
      : '';

    // Seaborn output
    if (config.outputFormat === 'seaborn') {
      const orderCode = config.barOrdering === 'ascending' 
        ? `\ndf = df.sort_values('value', ascending=True)`
        : config.barOrdering === 'descending'
          ? `\ndf = df.sort_values('value', ascending=False)`
          : '';
      
      const paletteCode = hasColorBy
        ? `# Color by '${dataContext.mapping.colorBy}' column\nunique_groups = df['color_group'].unique()\ngroup_palette = ${JSON.stringify(colors.slice(0, 10))}\ncolor_map = {g: group_palette[i % len(group_palette)] for i, g in enumerate(unique_groups)}\npalette = [color_map[g] for g in df['color_group']]`
        : usesPalette
          ? `palette = ${JSON.stringify(colors)}  # Active chart palette`
          : `palette = ['${config.color || '#4C6EE6'}'] * len(df)  # Single color`;
      
      return `${seabornSetup(s)}

# ======== DATA ========
${hasUserData ? `df = pd.read_csv('${dataContext.fileName}')
df = df.rename(columns={'${dataContext.mapping.category}': 'category', '${dataContext.mapping.value}': 'value'${hasColorBy ? `, '${dataContext.mapping.colorBy}': 'color_group'` : ''}})` : `df = pd.DataFrame({
    'category': ${JSON.stringify(categories)},
    'value': [${values.join(', ')}]
})`}
# ======================${orderCode}
${seabornCreateFigure(5.5, 4.5, 300, s)}

# Define colors
${paletteCode}

# Create bar chart
sns.barplot(data=df, x='category', y='value', palette=palette,
            edgecolor='${s?.axisColor || '#000000'}', linewidth=1.0, ax=ax)
${seabornTitleCode(config, s)}
${seabornAxisLabelsCode(config, s)}
${seabornLabelRotationCode(config)}

# Add grid
${seabornGridCode(config, 'y', s)}

${config.showValues ? `# Add value labels on bars
for container in ax.containers:
    ax.bar_label(container, fmt='%.${config.valueDecimals || 1}f', fontsize=10, color='${s?.foreground || '#000000'}', padding=3)` : '# Value labels disabled'}
${hasColorBy ? `\n# Add legend for color groups\nfrom matplotlib.patches import Patch\nlegend_elements = [Patch(facecolor=color_map[g], label=g) for g in unique_groups]\nax.legend(handles=legend_elements, loc='upper right', fontsize=${s?.legendSize || 10})` : ''}

# Set y-axis range
${seabornYAxisRangeCode(config)}
${seabornReferenceLineCode(config, 'horizontal', s)}
${seabornSpineCode(s, 'label', 'number')}
${seabornFinishCode('vertical_bar_chart')}`;
    }
    
    // Matplotlib output (default)
    return `${matplotlibSetup(s)}

# ======== DATA ========
${hasUserData ? `df = pd.read_csv('${dataContext.fileName}')
categories = df['${dataContext.mapping.category}'].tolist()
values = df['${dataContext.mapping.value}'].tolist()${hasColorBy ? `\ncolor_groups = df['${dataContext.mapping.colorBy}'].tolist()` : ''}` : `categories = ${JSON.stringify(categories)}  # Category labels
values = [${values.join(', ')}]  # Values`}
# ======================
${sortingCode(config, 'values', 'categories')}
${createFigure(5.5, 4.5, 300, s)}

${hasColorBy ? `# Color by group\nunique_groups = list(dict.fromkeys(color_groups))\ngroup_palette = ${JSON.stringify(colors.slice(0, 10))}\ncolor_map = {g: group_palette[i % len(group_palette)] for i, g in enumerate(unique_groups)}\nbar_colors = [color_map[g] for g in color_groups]` : generateColorCode(config, 'bar_colors', s)}${overridesCode}

# Create bar chart
bars = ax.bar(categories, values, color=bar_colors, width=0.6,
              edgecolor='${s?.axisColor || '#000000'}', linewidth=1.0)
${titleCode(config, s)}
${axisLabelsCode(config, s)}
${labelRotationCode(config)}

# Add grid
${gridCode(config, 'y', s)}

${valueLabelsCode(config, 'vertical', s)}
${hasColorBy ? `\n# Add legend for color groups\nfrom matplotlib.patches import Patch\nlegend_elements = [Patch(facecolor=color_map[g], label=g) for g in unique_groups]\nax.legend(handles=legend_elements, loc='upper right', fontsize=${s?.legendSize || 10})` : ''}

# Set y-axis range
${yAxisRangeCode(config)}
${referenceLineCode(config, 'horizontal', s)}
${spineCode(s, 'label', 'number')}
${finishCode('vertical_bar_chart')}`;
  },
};

export default verticalBar;
